<template>
  <div class="restore-point-tab">
    <div class="q-pl-md">
      <mi-infinite-scroll
        ref="miInfiniteScroll"
        class="mi-infinite-scroll"
      >
        <div class="restore-point-tab__empty-state">
          <p>Add a restore point selection to access again this part of the configuration later on.</p>
          <div
            v-if="!loadingCreateRestorePoint && restorePointsList.length < 1"
            class="restore-point-box no-restore-point"
          >
            <p>No restore point</p>
          </div>
        </div>

        <div v-if="restorePointsList.length >= 1 || loadingCreateRestorePoint">
          <restore-point-box
            v-for="restorePoint in restorePointsList"
            :key="restorePoint.name"
            class="restore-point-box"
            :restore-point="restorePoint"
            :list-restore-points="listRestorePoints"
            :configuration-id="configuration.id"
            :product-model-encoded-business-name="configuration.productModel.encodedBusinessName"
            :apply-restore-point-in-progress="applyRestorePointInProgress"
            :toggle-open-side-drawer="toggleOpenSideDrawer"
            :change-recently-applied="lastRestorePointRecentlyApplied"
            :is-recently-applied="isRecentlyApplied === restorePoint.id"
            :is-user-authorized="isUserAuthorized"
            @close-side-bar="toggleOpenSideDrawer"
            @update:apply-restore-point-in-progress="value => applyRestorePointInProgress = value"
          >
          </restore-point-box>
          <div v-if="loadingCreateRestorePoint" class="restore-point-box loading-restore-point">
            <mi-spinner size="24px">
            </mi-spinner>
          </div>
        </div>
        <div ref="scrollAnchor"></div>
      </mi-infinite-scroll>
    </div>

    <div class="add-restore-point-btn">
      <mi-btn
        :disable="loadingCreateRestorePoint || !isUserAuthorized"
        icon="plus"
        icon-size="10.25px"
        @click="addRestorePoint"
      >
        Add a restore point
      </mi-btn>
    </div>
  </div>

  <configuration-restore-empty-dialog
    :create-restore-point="createRestorePoint"
    :model-value="isConfigRestoreEmptyDialogShown"
    @update:model-value="value => isConfigRestoreEmptyDialogShown = value"
  ></configuration-restore-empty-dialog>
</template>

<script>

  import { nextTick, onMounted, ref } from 'vue'
  import { useStore } from 'vuex'
  import MiBtn from '../../../../../packages/@mi-library/MiBtn/MiBtn.vue'
  import {
    createConfigurationRestorePoint,
    getConfigurationRestorePoints,
    showHTTPErrorNotification
  } from '../../../../../api'
  import notify from '../../../../../utils/notify'
  import * as notificationTypes from '../../../../../plugins/quasar/notifications/notificationTypes'
  import ConfigurationRestoreEmptyDialog from '../ConfigurationRestoreEmptyDialog.vue'
  import MiSpinner from '../../../../../packages/@mi-library/MiSpinner/MiSpinner.vue'
  import MiInfiniteScroll from '../../../../../packages/@mi-library/MiInfiniteScroll/MiInfiniteScroll.vue'
  import RestorePointBox from './ConfigurationRestorePointBox.vue'

  export default {
    name: 'ConfigRestorePointTab',
    components: { RestorePointBox, MiInfiniteScroll, MiSpinner, ConfigurationRestoreEmptyDialog, MiBtn },
    props: {
      configuration: {
        type: Object,
        required: true
      },
      toggleOpenSideDrawer: {
        type: Function,
        required: true
      },
      configurationAllowedUsersList: {
        type: String,
        required: true,
        default: ''
      }
    },
    setup(props) {
      const store = useStore()
      const isConfigRestoreEmptyDialogShown = ref(false)
      const loadingCreateRestorePoint = ref(false)
      const restorePointsList = ref([])
      const scrollAnchor = ref(null)
      const applyRestorePointInProgress = ref(false)
      const isRecentlyApplied = ref('')
      const { user } = store.state

      const isUserAuthorized = props.configuration.authorizedUsers.includes(user.username)
        || props.configuration.authorizedUsers.length === 0

      const listRestorePoints = async () => {
        try {
          restorePointsList.value = await getConfigurationRestorePoints(props.configuration.id)
        }
        catch (err) {
          showHTTPErrorNotification(
            { status: err.status, message: err.message }
          )
        }
      }

      const lastRestorePointRecentlyApplied = restorePointId => {
        isRecentlyApplied.value = restorePointId
      }

      const createRestorePoint = async () => {
        try {
          loadingCreateRestorePoint.value = true

          await nextTick()
          if (scrollAnchor.value?.scrollIntoView) {
            scrollAnchor.value.scrollIntoView({ behavior: 'smooth' })
          }

          await createConfigurationRestorePoint(props.configuration)
          await store.dispatch('pac/saveConfiguration', props.configuration)

          notify({
            content: 'Restore point added',
            type: notificationTypes.BASIC_INFO,
            position: 'bottom'
          })
        }
        catch (e) {
          showHTTPErrorNotification(
            {
              status: 'RESTORE POINT HAS FAILED',
              message: 'Try to add again.',
              custom: true
            }
          )
        }
        finally {
          await listRestorePoints()
          loadingCreateRestorePoint.value = false
        }
      }

      const addRestorePoint = () => {
        if (props.configuration.selectedChoices.length > 0) {
          createRestorePoint()
        }
        else {
          isConfigRestoreEmptyDialogShown.value = true
        }
      }

      onMounted(() => {
        listRestorePoints()
      })

      return {
        addRestorePoint,
        isConfigRestoreEmptyDialogShown,
        createRestorePoint,
        loadingCreateRestorePoint,
        restorePointsList,
        scrollAnchor,
        applyRestorePointInProgress,
        lastRestorePointRecentlyApplied,
        isRecentlyApplied,
        listRestorePoints,
        user,
        isUserAuthorized
      }
    }
  }
</script>

<style lang="scss" scoped>
  .restore-point-tab {
    .mi-infinite-scroll {
      overflow: auto;
      overflow-y: scroll;
      max-height: calc(100vh - 300px);
    }

    .mi-infinite-scroll::-webkit-scrollbar {
      width: 0;
    }

    .mi-infinite-scroll::-webkit-scrollbar-thumb {
      background-color: #ffffff;
    }

    .restore-point-box {
      min-height: 70px;
      width: 323px;
      margin-bottom: 8px;
    }

    &__empty-state {
      line-height: 150%;
      overflow: hidden;

      .no-restore-point {
        align-content: center;
        text-align: center;
        color: #303c49;
        font-size: 14px;
        font-weight: 700;
        font-family: $mi-typography-font-family-condensed;
        letter-spacing: .8px;
      }
    }

    .mi-btn {
      height: 32px;
      width: 323px;
      font-size: 14px;
      justify-content: center;
    }

    ::v-deep(.q-btn__content) {
      gap: 4px;
    }

    .loading-restore-point {
      align-content: center;
      text-align: center;
      border: solid 1px #cbd3dc;
    }

    .add-restore-point-btn {
      margin-top: 8px;
      height: 60px;
      width: 355px;
      padding: 12px 16px;
      border-top: solid 1px $mi-anthracite-50;
      z-index: 1;
    }
  }
</style>
